import type { MisconceptionDefinition } from '../types'

/**
 * TAA — Signaalwoord sub-registry.
 *
 * Misconcepties rond het herkennen en classificeren van signaalwoorden
 * (woordsoort-verwarring en categorie-verwarring tussen oorzaak,
 * tijd, toevoeging en tegenstelling). Family `TAA-SIGNAALWOORD`.
 *
 * Onderdeel van de TAA-barrel-splitsing (juni 2026).
 */
export const TAA_SIGNAALWOORD_MISCONCEPTIONS: MisconceptionDefinition[] = [
  {
    id: 'taa.signaal-werkwoord-verward',
    label: 'Verwart werkwoord met signaalwoord',
    module: 'Taalbegrip',
    description: 'Leerling kiest een werkwoord in plaats van een signaalwoord.',
    typicalFamilies: ['TAA-SIGNAALWOORD'],
  },
  {
    id: 'taa.signaal-bnw-verward',
    label: 'Verwart bijvoeglijk naamwoord met signaalwoord',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een bijvoeglijk naamwoord in plaats van een signaalwoord.',
    typicalFamilies: ['TAA-SIGNAALWOORD'],
  },
  {
    id: 'taa.signaal-lidwoord-verward',
    label: 'Verwart lidwoord met signaalwoord',
    module: 'Taalbegrip',
    description: 'Leerling kiest een lidwoord in plaats van een signaalwoord.',
    typicalFamilies: ['TAA-SIGNAALWOORD'],
  },
  {
    id: 'taa.signaal-oorzaak-als-tijd',
    label: 'Verwart oorzaak-signaalwoord met tijd',
    module: 'Taalbegrip',
    description:
      'Leerling ziet een signaalwoord van oorzaak aan voor een signaalwoord van tijd.',
    typicalFamilies: ['TAA-SIGNAALWOORD'],
  },
  {
    id: 'taa.signaal-oorzaak-als-tegenstelling',
    label: 'Verwart oorzaak-signaalwoord met tegenstelling',
    module: 'Taalbegrip',
    description:
      'Leerling ziet een signaalwoord van oorzaak aan voor een signaalwoord van tegenstelling.',
    typicalFamilies: ['TAA-SIGNAALWOORD'],
  },
  {
    id: 'taa.signaal-oorzaak-als-toevoeging',
    label: 'Verwart oorzaak-signaalwoord met toevoeging',
    module: 'Taalbegrip',
    description:
      'Leerling ziet een signaalwoord van oorzaak aan voor een signaalwoord van toevoeging.',
    typicalFamilies: ['TAA-SIGNAALWOORD'],
  },
  {
    id: 'taa.signaal-reden-als-tijd',
    label: 'Verwart reden-signaalwoord met tijd',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een reden-signaalwoord waar een tijdssignaalwoord past.',
    typicalFamilies: ['TAA-SIGNAALWOORD'],
  },
  {
    id: 'taa.signaal-toevoeging-als-tijd',
    label: 'Verwart toevoeging met tijd',
    module: 'Taalbegrip',
    description:
      'Leerling ziet een signaalwoord van toevoeging aan voor een signaalwoord van tijd.',
    typicalFamilies: ['TAA-SIGNAALWOORD'],
  },
  // ─── Seed-upgrade (Phase B, juni 2026) ──────────────────────────────────
  {
    id: 'taa.signaal-bijwoord-verward',
    label: 'Verwart bijwoord met signaalwoord',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een bijwoord (zoals "hard", "snel") in plaats van een signaalwoord.',
    typicalFamilies: ['TAA-SIGNAALWOORD'],
  },
  {
    id: 'taa.signaal-voorzetsel-verward',
    label: 'Verwart voorzetsel met signaalwoord',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een voorzetsel (zoals "naar", "op") in plaats van een signaalwoord.',
    typicalFamilies: ['TAA-SIGNAALWOORD'],
  },
  {
    id: 'taa.signaal-voornaamwoord-verward',
    label: 'Verwart voornaamwoord met signaalwoord',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een persoonlijk voornaamwoord (zoals "we", "hij") in plaats van een signaalwoord.',
    typicalFamilies: ['TAA-SIGNAALWOORD'],
  },
  {
    id: 'taa.signaal-tegenstelling-als-tijd',
    label: 'Verwart tegenstelling-signaalwoord met tijd',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een tegenstelling-signaalwoord ("maar", "toch") waar een tijdssignaalwoord past.',
    typicalFamilies: ['TAA-SIGNAALWOORD'],
  },
  {
    id: 'taa.signaal-tegenstelling-als-oorzaak',
    label: 'Verwart tegenstelling met oorzaak-gevolg',
    module: 'Taalbegrip',
    description:
      'Leerling ziet een tegenstelling-signaalwoord aan voor een oorzaak-gevolg-signaalwoord.',
    typicalFamilies: ['TAA-SIGNAALWOORD'],
  },
  {
    id: 'taa.signaal-tegenstelling-als-toevoeging',
    label: 'Verwart tegenstelling met toevoeging',
    module: 'Taalbegrip',
    description:
      'Leerling ziet een tegenstelling-signaalwoord aan voor een toevoeging-signaalwoord.',
    typicalFamilies: ['TAA-SIGNAALWOORD'],
  },
  {
    id: 'taa.signaal-tijd-als-tegenstelling',
    label: 'Verwart tijd-signaalwoord met tegenstelling',
    module: 'Taalbegrip',
    description:
      'Leerling ziet een tijd-signaalwoord aan voor een tegenstelling-signaalwoord.',
    typicalFamilies: ['TAA-SIGNAALWOORD'],
  },
  {
    id: 'taa.signaal-tijd-als-oorzaak',
    label: 'Verwart tijd met oorzaak-gevolg',
    module: 'Taalbegrip',
    description:
      'Leerling ziet een tijd-signaalwoord aan voor een oorzaak-gevolg-signaalwoord.',
    typicalFamilies: ['TAA-SIGNAALWOORD'],
  },
  {
    id: 'taa.signaal-tijd-als-toevoeging',
    label: 'Verwart tijd met toevoeging',
    module: 'Taalbegrip',
    description:
      'Leerling ziet een tijd-signaalwoord aan voor een toevoeging-signaalwoord.',
    typicalFamilies: ['TAA-SIGNAALWOORD'],
  },
  {
    id: 'taa.signaal-toevoeging-als-tegenstelling',
    label: 'Verwart toevoeging met tegenstelling',
    module: 'Taalbegrip',
    description:
      'Leerling ziet een toevoeging-signaalwoord aan voor een tegenstelling-signaalwoord.',
    typicalFamilies: ['TAA-SIGNAALWOORD'],
  },
  {
    id: 'taa.signaal-toevoeging-als-oorzaak',
    label: 'Verwart toevoeging met oorzaak-gevolg',
    module: 'Taalbegrip',
    description:
      'Leerling ziet een toevoeging-signaalwoord aan voor een oorzaak-gevolg-signaalwoord.',
    typicalFamilies: ['TAA-SIGNAALWOORD'],
  },
]
